/**
 * Parse the `/uconfig.php` settings page. We only need two signals:
 *
 *   1. Is the user logged in? Combination of three independent markers,
 *      each of which is only rendered to authenticated sessions:
 *
 *        a. A "Log Out" anchor (the historical EhViewer signal — still
 *           present on some legacy layouts / mobile views).
 *        b. A logged-in-only nav entry in the top `#nb` bar: `My Tags`
 *           (`/mytags`), `My Uploads` (`upld.exhentai.org/upld/manage`
 *           or `upload.e-hentai.org/manage`), or `My Home` (`/home.php`).
 *        c. The settings form's Apply submit (`#outer …
 *           input[value="Apply"]`). Anonymous users either get a Sad
 *           Panda (exhentai → empty body, handled by the early return
 *           and by `detectErrorFromResponse`) or a 302 to the forum
 *           login (caught by `assertOk` before this parser runs), so
 *           the Apply submit only ever ships in logged-in HTML.
 *
 *      Any one of these is sufficient. We OR them together because
 *      the e-hentai team has shipped at least two markup revisions
 *      that toggled which markers are present.
 *
 *   2. On exhentai only: is the `igneous` cookie effectively present?
 *      The site itself doesn't echo the cookie, but an empty body on
 *      `exhentai.org` is the canonical "Sad Panda" / missing-igneous
 *      signal — handled in `errors.ts`'s `detectErrorFromResponse`, not
 *      here. This parser only checks the HTML we DID get.
 *
 * Username extraction is best-effort. The current `/uconfig.php` layout
 * does NOT expose the raw username anywhere; it's only available via
 * `/home.php`. We keep the historical `<a href="/home.php">username</a>`
 * extractor for back-compat in case the old layout returns, scoped to
 * anchors inside the `#nb` top navbar so the page's secondary `#lb`
 * panel — which contains an unrelated "[Overview]" → `/home.php` link
 * on e-hentai's uconfig — never gets mistaken for a username.
 * `validateAuth` accepts `{ ok: true }` without a username and the SPA
 * renders fine without one.
 */

import { parseDocument, textOf } from '@/lib/html';

export interface UConfigParseResult {
  loggedIn: boolean;
  /**
   * The username shown in the header (`<a href="/home.php">username</a>`).
   * Only set on the legacy layout; the current layout does not expose
   * it on `/uconfig.php` at all.
   */
  username?: string;
}

/**
 * Hrefs that the site only ever renders for an authenticated session.
 * Matched against the `#nb` top-bar navigation as a logged-in confirmation.
 *
 * - `/mytags`         — personal tag namespace, login-gated.
 * - `upld.exhentai.org/upld/manage` / `upload.e-hentai.org/manage` —
 *   the "My Uploads" link. Anonymous nav omits it entirely.
 * - `/home.php`       — "My Home" page; anonymous users have no home.
 */
const LOGGED_IN_NAV_PATTERNS: RegExp[] = [
  /\/mytags(?:$|\?|#)/i,
  /\/(?:upld|upload)\b[^"']*\/manage/i,
  /\/home\.php(?:$|\?|#)/i,
];

export function parseUConfig(html: string): UConfigParseResult {
  if (!html || !html.trim()) {
    return { loggedIn: false };
  }
  const doc = parseDocument(html);

  let loggedIn = false;
  let username: string | undefined;

  const anchors = Array.from(doc.querySelectorAll('a')) as HTMLAnchorElement[];
  // Restrict username scanning to the top navbar (`#nb`). The page's
  // secondary settings panel (`#lb`) contains its own `/home.php`
  // anchor with text "Overview" that is NOT a username.
  const navbar = doc.querySelector('#nb');
  const navAnchors = navbar
    ? (Array.from(navbar.querySelectorAll('a')) as HTMLAnchorElement[])
    : [];
  const navAnchorSet = new Set<HTMLAnchorElement>(navAnchors);

  for (const a of anchors) {
    const href = a.getAttribute('href') ?? '';
    const text = textOf(a);

    if (/logout/i.test(href) || /^log\s*out$/i.test(text)) {
      loggedIn = true;
      continue;
    }

    if (LOGGED_IN_NAV_PATTERNS.some((re) => re.test(href))) {
      loggedIn = true;
      // Legacy: when the username was rendered as the /home.php anchor
      // in `#nb` (text != a known sidebar label), capture it. The
      // current layouts use either "My Home" (e-hentai navbar) or no
      // /home.php link at all (exhentai), so this is a no-op in
      // practice — but keep it for back-compat / forward-compat.
      if (
        navAnchorSet.has(a) &&
        /\/home\.php(?:$|\?|#)/i.test(href) &&
        text &&
        !/^(my\s+home|home|overview)$/i.test(text)
      ) {
        username ??= text;
      }
    }
  }

  // Settings-form Apply submit. `#outer` works for both the old
  // `<form id="outer">` markup and the current `<div id="outer">`
  // wrapper. The `name="apply"` attribute is more stable than
  // matching on the `value` (display text could be localized).
  if (
    !loggedIn &&
    (doc.querySelector('#outer input[name="apply"]') ||
      doc.querySelector('#outer input[type="submit"][value="Apply"]'))
  ) {
    loggedIn = true;
  }

  // Welcome / logged-out page: the only auth-related anchor is a link
  // to the forum login. If we never saw any logged-in marker AND that
  // login link is on the page, we're definitely out.
  if (!loggedIn) {
    const loginLink = anchors.find((a) =>
      /forums\.e-hentai\.org\/index\.php\?act=Login/i.test(a.getAttribute('href') ?? ''),
    );
    if (loginLink) return { loggedIn: false };
  }

  if (loggedIn && username) {
    return { loggedIn, username };
  }
  return { loggedIn };
}
